import {
  Align,
  Button,
  Container,
  IconButton,
  Loader,
  Spacer,
  SvgArrowLeft,
  SvgArrowRight,
  SvgCheck,
  Text,
  TextButton,
} from '@loomhq/lens';
import classnames from 'classnames';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';

import { COMPLETED_ONBOARDING_STEP } from 'constants/events';
import { SLACKID_ONBOARDING_MODAL } from 'constants/onboarding';
import {
  REQUEST_TO_JOIN_WORKSPACE_ALREADY_REQUESTED,
  REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED,
  REQUEST_TO_JOIN_WORKSPACE_REQUESTED,
} from 'constants/organizationInvitations';
import * as analytics from 'utilities/analytics';
import {
  getSuggestedWorkspacesQuery,
  requestToJoinWorkspace,
} from 'utilities/welcome';

import { SUGGESTED_WORKSPACES_STEP } from 'components/welcome-webapp-simplified/constants';
import WorkspaceLogo from 'components/workspace-logo';

import styles from './styles.module.less';

function WorkspaceListItem({
  workspace,
  autoJoin,
  isSuggested,
  isSelected,
  setSelected,
  setRequestedJoin,
  onRequestClick,
}) {
  const memberCount = workspace.counts?.users?.total_active_workspace_users;

  function handleClick() {
    if (!autoJoin) {
      setRequestedJoin(true);
    }

    // If the user clicks on the suggested workspace don't make them click "Next" too
    // automatically take them to the next step
    if (isSuggested) {
      onRequestClick(workspace.id);
    }

    setSelected(workspace.id);
  }

  return (
    <div className={styles.workspaceListItem}>
      <div
        className={classnames(
          'flex items:center width:full justify:spaceBetween p:medium'
        )}
        role="listitem"
        tabIndex={0}
        aria-label={workspace.name}
      >
        <div className="shrink:0">
          <WorkspaceLogo workspace={workspace} size={6} />
        </div>

        <div className="flex flexDirection:column text:left mx:small grow:1 ellipsis">
          <div className="flex">
            <Text fontWeight="bold" size="medium" hasEllipsis>
              {workspace.name}
            </Text>
          </div>
          <span className="text:small c:bodyDimmed">
            {memberCount} {pluralize('Member', memberCount)}
          </span>
        </div>
      </div>

      <Button
        onClick={handleClick}
        variant="primary"
        hasFullWidth
        icon={isSelected ? <SvgCheck /> : null}
        id="joinButton"
        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      >
        {autoJoin ? 'Join' : 'Request Access'}
      </Button>
    </div>
  );
}

function WorkspaceList({
  workspaces,
  showMoreWorkspaces,
  setShowMoreWorkspaces,
  selected,
  setSelected,
  setRequestedJoin,
  onRequestClick,
}) {
  if (showMoreWorkspaces) {
    return (
      <>
        <Container
          borderSide="all"
          radius="medium"
          height="15rem"
          padding="small"
          overflow="scroll"
        >
          {workspaces.map(workspace => {
            const space = workspace?.workspace;

            return (
              <WorkspaceListItem
                key={space.id}
                workspace={space}
                autoJoin={workspace?.autoJoin}
                isSelected={selected === space?.id}
                setSelected={setSelected}
                setRequestedJoin={setRequestedJoin}
              />
            );
          })}
        </Container>
        <Spacer bottom="medium" />
      </>
    );
  }

  return (
    <>
      <WorkspaceListItem
        {...workspaces[0]}
        isSuggested
        isSelected={workspaces[0].workspace.id === selected}
        setSelected={setSelected}
        setRequestedJoin={setRequestedJoin}
        onRequestClick={onRequestClick}
      />
      <Spacer bottom="medium" />
      <Text alignment="left">
        <TextButton onClick={setShowMoreWorkspaces}>
          See more Workspaces
        </TextButton>{' '}
        or
      </Text>
    </>
  );
}

function SuggestedWorkspaceStep({
  handleNextStep,
  handlePrevStep,
  setFoundSuggestedWorkspaces,
  setRequestedJoin,
}) {
  const [workspaces, setWorkspaces] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);

  const [showMoreWorkspaces, setShowMoreWorkspaces] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    async function getSuggestedWorkspaces() {
      const { data } = await getSuggestedWorkspacesQuery();

      if (!data?.result?.workspaces?.length) {
        // No suggested workspaces, move on to the next step.
        analytics.track(COMPLETED_ONBOARDING_STEP, {
          step: SUGGESTED_WORKSPACES_STEP,
          action: 'no suggestions',
          source: SLACKID_ONBOARDING_MODAL,
        });

        setFoundSuggestedWorkspaces(false);
        handleNextStep();
      } else {
        setFoundSuggestedWorkspaces(true);
      }

      setWorkspaces(data);
      setLoading(false);
    }

    getSuggestedWorkspaces();
  }, [handleNextStep, setFoundSuggestedWorkspaces]);

  async function onRequestClick(selectedWorkspace) {
    // If selectedWorkspace passed as param use that, otherwise use workspace selected in state
    const workspaceId = selectedWorkspace ?? selected;

    setRequestLoading(true);

    const {
      data: { result },
    } = await requestToJoinWorkspace(workspaceId);

    const eventProperties = {
      step: SUGGESTED_WORKSPACES_STEP,
      organization_id: workspaceId,
      source: SLACKID_ONBOARDING_MODAL,
    };

    if (
      [
        REQUEST_TO_JOIN_WORKSPACE_REQUESTED,
        REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED,
        REQUEST_TO_JOIN_WORKSPACE_ALREADY_REQUESTED,
      ].includes(result.status)
    ) {
      if (result.status === REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED) {
        eventProperties.action = 'autojoined';
        setRequestedJoin(false);
        handleNextStep(2); // Skip create-workspace step
      } else {
        eventProperties.action = 'requested';
        setRequestedJoin(true);
        handleNextStep(); // User must create workspace while waiting on request
      }

      setRequestLoading(false);
      analytics.track(COMPLETED_ONBOARDING_STEP, eventProperties);
    }
  }

  if (!workspaces || loading) {
    return (
      <Align alignment="center">
        <Loader size="large" />
      </Align>
    );
  }

  return (
    <>
      {!showMoreWorkspaces && (
        <>
          <Text size="large" fontWeight="bold">
            We found your team!
          </Text>
          <Spacer bottom="small" />
          <Text size="medium">
            Join your team’s Workspace and gain instant access to team videos
            and see deeper video insights.
          </Text>
          <Spacer bottom="small" />
        </>
      )}

      {workspaces?.result && (
        <WorkspaceList
          workspaces={workspaces.result.workspaces}
          showMoreWorkspaces={showMoreWorkspaces}
          setShowMoreWorkspaces={() => setShowMoreWorkspaces(true)}
          selected={selected}
          setSelected={setSelected}
          setRequestedJoin={setRequestedJoin}
          onRequestClick={onRequestClick}
        />
      )}
      {showMoreWorkspaces && (
        <Text>
          <TextButton onClick={() => setShowMoreWorkspaces(false)}>
            Show fewer Workspaces
          </TextButton>{' '}
          or
        </Text>
      )}
      <Spacer bottom="small" />
      <Button variant="neutral" hasFullWidth onClick={() => handleNextStep()}>
        Create a new Workspace
      </Button>
      <Spacer bottom="medium" />

      <div className="flex justify:spaceBetween">
        <IconButton
          altText="back"
          icon={<SvgArrowLeft />}
          onClick={() => handlePrevStep()}
        />
        <Button
          isDisabled={!selected || requestLoading}
          icon={<SvgArrowRight />}
          iconPosition="right"
          onClick={() => onRequestClick()}
          hasLoader={requestLoading}
        >
          Next
        </Button>
      </div>
    </>
  );
}

export default SuggestedWorkspaceStep;
